/**
 * Zod schemas for configuration validation
 * Provides runtime validation for YAML configuration files
 */

import { z } from "zod";

/**
 * Server configuration schema
 */
export const ServerConfigSchema = z.object({
  listen: z
    .string()
    .regex(/^[\w.-]+:\d+$/, "Must be in format host:port")
    .default("127.0.0.1:8787"),
  timeout_ms: z.number().int().positive().default(300000),
});

/**
 * Routing condition schema
 */
export const RoutingConditionSchema = z.object({
  keywords: z.array(z.string()).optional(),
  patterns: z.array(z.string()).optional(),
  min_tokens: z.number().int().nonnegative().optional(),
  max_tokens: z.number().int().positive().optional(),
  models: z.array(z.string()).optional(),
  has_tools: z.boolean().optional(),
});

/**
 * Route destination schema
 */
export const RouteDestinationSchema = z.object({
  provider: z.string().min(1),
  model: z.string().optional(),
});

/**
 * Routing rule schema
 */
export const RoutingRuleConfigSchema = z.object({
  name: z.string().min(1),
  conditions: RoutingConditionSchema,
  route_to: RouteDestinationSchema,
  priority: z.number().int().default(0),
  enabled: z.boolean().default(true),
});

/**
 * Routing configuration schema
 */
export const RoutingConfigSchema = z.object({
  default_provider: z.string().min(1),
  default_model: z.string().optional(),
  rules: z.array(RoutingRuleConfigSchema).default([]),
});

/**
 * API key configuration schema
 */
export const ApiKeyConfigSchema = z.object({
  key: z.string().min(1),
  weight: z.number().positive().optional(),
});

/**
 * Provider type enum
 */
export const ProviderTypeSchema = z.enum([
  "anthropic",
  "zai",
  "ollama",
  "openai",
  "gemini",
  "grok",
  "mistral",
  "deepseek",
  "kimi",
]);

/**
 * Provider configuration schema
 */
export const ProviderConfigSchema = z.object({
  name: z.string().min(1),
  type: ProviderTypeSchema,
  enabled: z.boolean().default(true),
  base_url: z.string().url().optional(),
  keys: z.array(ApiKeyConfigSchema).optional(),
  subscription_passthrough: z.boolean().optional(),
  model_aliases: z.record(z.string()).optional(),
  default_model: z.string().optional(),
});

/**
 * Circuit breaker configuration schema
 */
export const CircuitBreakerConfigSchema = z.object({
  failure_threshold: z.number().int().positive().default(3),
  recovery_timeout_seconds: z.number().int().positive().default(60),
});

/**
 * Health configuration schema
 */
export const HealthConfigSchema = z.object({
  check_interval_seconds: z.number().int().positive().default(30),
  circuit_breaker: CircuitBreakerConfigSchema.default({}),
});

/**
 * Log level schema
 */
export const LogLevelSchema = z.enum(["debug", "info", "warn", "error"]);

/**
 * Log format schema
 */
export const LogFormatSchema = z.enum(["text", "json"]);

/**
 * Logging configuration schema
 */
export const LoggingConfigSchema = z.object({
  level: LogLevelSchema.default("info"),
  format: LogFormatSchema.default("text"),
});

/**
 * Complete gateway configuration schema
 */
export const GatewayConfigSchema = z.object({
  server: ServerConfigSchema.default({}),
  routing: RoutingConfigSchema,
  providers: z.array(ProviderConfigSchema).min(1),
  health: HealthConfigSchema.default({}),
  logging: LoggingConfigSchema.default({}),
});

/**
 * Inferred types from schemas
 */
export type ValidatedGatewayConfig = z.infer<typeof GatewayConfigSchema>;
export type ValidatedProviderConfig = z.infer<typeof ProviderConfigSchema>;
export type ValidatedRoutingRule = z.infer<typeof RoutingRuleConfigSchema>;
